import { useEffect, useRef, useState, type RefObject } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router";
import { Avatar, Button, Cluster, Drawer, Input, Stack, useToast } from "@salesapp/ui";
import { APP_REGISTRY, type AppRegistryEntry } from "../lib/appRegistry";
import { appUrl } from "../lib/customerUrl";
import { planNavLabel } from "../lib/planPricing";
import { useAuth } from "./AuthProvider";
import { useAccess } from "./AccessProvider";
import { useOptionalGuide } from "../guide/GuideProvider";
import { isAccountMenuTarget } from "../guide/playbooks";
import { useOptionalLoxley } from "../loxley/LoxleyProvider";
import { helpMemory, isHelpPath } from "../help/helpMemory";
import { AccountThemeToggle } from "./AccountThemeToggle";
import { getAppIcon } from "./appIcons";
import { opsApi, type AppNotification } from "../lib/opsApi";
import { useLocale } from "./LocaleProvider";
import { useNarrowViewport } from "../lib/useNarrowViewport";
import { crmApi, type SearchHit } from "../lib/crmApi";
import "./shell.css";

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" className="top-nav-menu-icon" aria-hidden>
      <circle cx="11" cy="11" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16l4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function HamburgerIcon() {
  return (
    <svg viewBox="0 0 24 24" className="top-nav-menu-icon" aria-hidden>
      <path
        d="M4 7h16M4 12h16M4 17h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AppLinks({
  apps,
  orgSlug,
  onNavigate,
  className,
}: {
  apps: AppRegistryEntry[];
  orgSlug: string | null | undefined;
  onNavigate?: () => void;
  className: string;
}) {
  return (
    <nav className={className} aria-label="Applications">
      {apps.map((entry) => {
        const to = entry.appCode === "dashboard" ? appUrl("crm", orgSlug, "/dashboard") : appUrl(entry.appCode, orgSlug);
        return (
          <NavLink
            key={entry.appCode}
            to={to}
            end={entry.appCode === "dashboard"}
            className={({ isActive }) => `top-nav-link${isActive ? " is-active" : ""}`}
            data-tour={`app-${entry.appCode}`}
            onClick={onNavigate}
          >
            <span className="top-nav-app-icon" aria-hidden>
              {getAppIcon(entry.appCode)}
            </span>
            {entry.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

/**
 * Top horizontal nav — the Sales App's structural signature vs. a heavy
 * left rail. Rendered entirely from APP_REGISTRY, so a new app is a new
 * data row, never a shell edit. Narrow viewports collapse apps into a
 * hamburger drawer so the canvas stays usable.
 */
export function TopNav() {
  const { session, logout } = useAuth();
  const { can, canApp, tier, isTrial, grantSource } = useAccess();
  const isPartner = grantSource === "billing_exempt";
  const guide = useOptionalGuide();
  const toast = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
        window.setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const narrow = useNarrowViewport();
  const planName = planNavLabel(tier);
  const visibleApps = APP_REGISTRY.filter((entry) => {
    if (entry.appCode === "dashboard") return true;
    if (entry.capability) return can(entry.capability);
    return canApp(entry.appCode);
  });
  const canOrgSettings = Boolean(session?.isOrgAdmin || can("crm.companies", "configure"));
  const location = useLocation();
  const navigateTo = useNavigate();
  const onHelp = isHelpPath(location.pathname);
  // Help has its own topic search; hiding the workspace search there stops
  // people typing a help question into the record search by mistake.
  const showSearch =
    Boolean(session?.orgId) &&
    !onHelp &&
    (can("crm.dashboard.view") || can("crm.companies") || can("crm.opportunities"));

  useEffect(() => {
    if (!isHelpPath(location.pathname)) helpMemory.rememberPage(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  // Help behaves as a toggle: open it where you left off, click again to go back.
  function toggleHelp(event: { preventDefault: () => void }) {
    event.preventDefault();
    if (onHelp) {
      navigateTo(helpMemory.returnPage() ?? appUrl("crm", session?.orgSlug, "/dashboard"));
      return;
    }
    navigateTo(`/help${helpMemory.lastTopic()}`);
  }

  return (
    <header className="top-nav">
      <button
        type="button"
        className="top-nav-menu-btn"
        aria-label="Open applications menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen(true)}
      >
        <HamburgerIcon />
      </button>

      <Link to={appUrl("crm", session?.orgSlug, "/dashboard")} className="top-nav-brand" aria-label="Go to My Stats">
        <img src="/logo-mark.svg" alt="" className="top-nav-mark" />
        <span className="top-nav-brand-copy">
          <span className="top-nav-brand-name">Saleslox</span>
          {planName ? (
            <span className="top-nav-plan" aria-label="Current plan">{planName}</span>
          ) : null}
          {isTrial ? (
            <span className="top-nav-trial" aria-label="Trial licence">Trial</span>
          ) : null}
          {isPartner ? (
            <span className="top-nav-partner" aria-label="Partner licence">Partner</span>
          ) : null}
        </span>
      </Link>

      <AppLinks
        apps={visibleApps}
        orgSlug={session?.orgSlug}
        className="top-nav-links top-nav-links--desktop"
      />

      {showSearch && session?.orgId ? (
        narrow && !searchOpen ? (
          <button
            type="button"
            className="top-nav-search-btn"
            aria-label="Search"
            onClick={() => setSearchOpen(true)}
          >
            <SearchIcon />
          </button>
        ) : (
          <GlobalSearch
            orgId={session.orgId}
            orgSlug={session.orgSlug}
            autoFocus={narrow && searchOpen}
            inputRef={searchInputRef}
            onDismiss={narrow ? () => setSearchOpen(false) : undefined}
          />
        )
      ) : null}

      <div className="top-nav-tools">
        <AccountThemeToggle />
        {session?.orgId && can("crm.dashboard.view") ? (
          <NotificationBell orgId={session.orgId} />
        ) : null}
        {narrow ? null : (
          <NavLink
            to="/help"
            className={({ isActive }) => `top-nav-chip${isActive ? " is-active" : ""}`}
            data-tour="nav-help"
            onClick={toggleHelp}
          >
            <span className="top-nav-chip-label">Help</span>
          </NavLink>
        )}
        {session ? (
          <AccountMenu
            email={session.email}
            orgName={session.orgName}
            orgSlug={session.orgSlug}
            canOrgSettings={canOrgSettings}
            showHelp={narrow}
            onToggleHelp={toggleHelp}
            onReplayTours={guide ? () => guide.replay() : undefined}
            onSignOut={() =>
              void logout().catch(() =>
                toast.show("Sign out could not be completed. Please try again.", "danger"),
              )
            }
          />
        ) : null}
      </div>

      <Drawer isOpen={menuOpen} onClose={() => setMenuOpen(false)} title="Applications" side="left">
        <AppLinks
          apps={visibleApps}
          orgSlug={session?.orgSlug}
          className="top-nav-drawer-links"
          onNavigate={() => setMenuOpen(false)}
        />
      </Drawer>
    </header>
  );
}

const SEARCH_PATHS: Record<string, (id: string) => string> = {
  company: (id) => `/companies/${id}`,
  lead: (id) => `/leads/${id}`,
  opportunity: (id) => `/opportunities/${id}`,
  quote: (id) => `/quotes/${id}`,
  order: (id) => `/orders/${id}`,
  invoice: (id) => `/invoices/${id}`,
  support: (id) => `/support/${id}`,
};

function hitPath(hit: SearchHit): string | null {
  if (hit.record_type === "contact") {
    if (!hit.company_id) return null;
    return `/companies/${hit.company_id}?contact=${encodeURIComponent(hit.record_id)}`;
  }
  return SEARCH_PATHS[hit.record_type]?.(hit.record_id) ?? null;
}

function GlobalSearch({
  orgId,
  orgSlug,
  autoFocus = false,
  inputRef,
  onDismiss,
}: {
  orgId: string;
  orgSlug: string | null;
  autoFocus?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  onDismiss?: () => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<SearchHit[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const needle = query.trim();
    if (needle.length < 2) {
      setItems([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void crmApi
        .search(orgId, needle)
        .then((result) => {
          setItems(result.items);
          setOpen(true);
        })
        .catch(() => setItems([]));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [orgId, query]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [items]);

  useEffect(() => {
    function onPointer(event: MouseEvent) {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        onDismiss?.();
      }
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [onDismiss]);

  function go(hit: SearchHit) {
    const path = hitPath(hit);
    if (!path) return;
    setOpen(false);
    setQuery("");
    onDismiss?.();
    navigate(appUrl("crm", orgSlug, path));
  }

  return (
    <div className="top-nav-search" ref={root}>
      <Input
        type="search"
        className="top-nav-search-input"
        autoComplete="off"
        placeholder="Search companies, leads, people, deals, quotes, orders, invoices, tickets"
        aria-label="Search companies, leads, people, deals, quotes, orders, invoices, and tickets"
        autoFocus={autoFocus}
        ref={inputRef}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && items.length > 0) {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((current) => Math.min(current + 1, items.length - 1));
            return;
          }
          if (event.key === "ArrowUp" && items.length > 0) {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((current) => Math.max(current - 1, 0));
            return;
          }
          const needle = query.trim();
          if (event.key === "Enter" && needle.length >= 2) {
            event.preventDefault();
            if (activeIndex >= 0 && items[activeIndex]) {
              go(items[activeIndex]);
              return;
            }
            setOpen(false);
            onDismiss?.();
            navigate(appUrl("crm", orgSlug, `/search?q=${encodeURIComponent(needle)}`));
          }
        }}
        onFocus={() => {
          if (items.length) setOpen(true);
        }}
      />
      {open && items.length > 0 && (
        <ul className="top-nav-search-results" role="listbox">
          {items.map((hit, index) => (
            <li key={`${hit.record_type}-${hit.record_id}`}>
              <button
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                className={`top-nav-search-hit${index === activeIndex ? " is-active" : ""}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => go(hit)}
              >
                <strong>{hit.title || "Untitled"}</strong>
                <span>{hit.subtitle}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && query.trim().length >= 2 && items.length === 0 && (
        <p className="top-nav-search-empty" role="status">No matching records</p>
      )}
    </div>
  );
}

function AccountMenu({
  email,
  orgName,
  orgSlug,
  canOrgSettings,
  showHelp = false,
  onToggleHelp,
  onReplayTours,
  onSignOut,
}: {
  email: string;
  orgName: string | null;
  orgSlug: string | null | undefined;
  canOrgSettings: boolean;
  showHelp?: boolean;
  onToggleHelp?: (event: { preventDefault: () => void }) => void;
  onReplayTours?: () => void;
  onSignOut: () => void;
}) {
  const guide = useOptionalGuide();
  const loxley = useOptionalLoxley();
  const tourTarget = guide?.step?.target;
  const tourMenu = isAccountMenuTarget(tourTarget);
  const tourAvatar = tourTarget === "account-menu" || tourMenu;
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const shown = open || tourMenu;

  useEffect(() => {
    if (!shown) return;
    function onPointer(event: MouseEvent) {
      if (tourMenu) return;
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [shown, tourMenu]);

  return (
    <div className={`account-menu${tourAvatar ? " is-tour-target" : ""}`} ref={root}>
      <button
        type="button"
        className="top-nav-signout"
        aria-haspopup="menu"
        aria-expanded={shown}
        aria-label={`Account menu for ${email}`}
        data-tour="account-menu"
        onClick={() => setOpen((value) => !value)}
      >
        <Avatar name={email} />
        {orgName ? (
          <span className="top-nav-org" title={orgName} aria-label="Current organisation">{orgName}</span>
        ) : null}
      </button>
      {shown && (
        <div className="account-menu-panel" role="menu">
          {orgName ? (
            <p className="account-menu-org" role="presentation">
              {orgName}
            </p>
          ) : null}
          {showHelp && onToggleHelp ? (
            <button
              type="button"
              role="menuitem"
              className="account-menu-item"
              data-tour="nav-help"
              onClick={(event) => {
                setOpen(false);
                onToggleHelp(event);
              }}
            >
              Help
            </button>
          ) : null}
          <NavLink
            to={appUrl("crm", orgSlug, "/account")}
            role="menuitem"
            className="account-menu-item"
            onClick={() => setOpen(false)}
          >
            User settings
          </NavLink>
          {canOrgSettings && (
            <NavLink
              to={appUrl("crm", orgSlug, "/settings")}
              role="menuitem"
              className="account-menu-item"
              data-tour="org-settings"
              onClick={() => setOpen(false)}
            >
              Organisation settings
            </NavLink>
          )}
          {loxley?.available && (
            <button
              type="button"
              role="menuitem"
              className="account-menu-item"
              data-tour="loxley-toggle"
              onClick={() => {
                setOpen(false);
                if (loxley.hidden) {
                  loxley.setHidden(false);
                  loxley.setOpen(true);
                } else {
                  loxley.setHidden(true);
                }
              }}
            >
              {loxley.hidden ? "Show Loxley" : "Hide Loxley"}
            </button>
          )}
          {onReplayTours && (
            <button
              type="button"
              role="menuitem"
              className="account-menu-item"
              data-tour="replay-tours"
              onClick={() => {
                setOpen(false);
                onReplayTours();
              }}
            >
              Guided tours
            </button>
          )}
          <a
            href="mailto:support@saleslox.com"
            title="Opens your email app to support@saleslox.com"
            role="menuitem"
            className="account-menu-item"
            onClick={() => setOpen(false)}
          >
            Contact support
          </a>
          <button type="button" role="menuitem" className="account-menu-item" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function NotificationBell({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { formatDateTime } = useLocale();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    void opsApi
      .listNotifications(orgId)
      .then((result) => setItems(result.notifications))
      .catch(() => setItems([]));
  }, [orgId, open]);

  const count = items.length;
  const label = count ? `Notifications (${count})` : "Notifications";

  function dismissAll() {
    setClearing(true);
    void opsApi
      .dismissAllNotifications(orgId)
      .then(() => setItems([]))
      .catch(() => toast.show("Notifications could not be cleared.", "danger"))
      .finally(() => setClearing(false));
  }

  return (
    <>
      <button
        type="button"
        className="top-nav-chip top-nav-chip--notify"
        onClick={() => setOpen(true)}
        aria-label={label}
      >
        <svg viewBox="0 0 24 24" className="top-nav-notify-icon" aria-hidden>
          <path
            d="M12 22a2.2 2.2 0 0 0 2.2-2.2h-4.4A2.2 2.2 0 0 0 12 22Zm7-6.2V11a7 7 0 1 0-14 0v4.8L3 17.6V19h18v-1.4l-2-1.8Z"
            fill="currentColor"
          />
        </svg>
        <span className="top-nav-chip-label">
          Notifications
          {count ? (
            <>
              {" ("}
              <span className="top-nav-notify-count-inline">{count}</span>
              {")"}
            </>
          ) : null}
        </span>
        {count ? (
          <span className="top-nav-notify-count" aria-hidden>
            {count}
          </span>
        ) : null}
      </button>
      <Drawer
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Notifications"
        actions={
          count ? (
            <Button type="button" variant="secondary" size="sm" disabled={clearing} onClick={dismissAll}>
              Dismiss all
            </Button>
          ) : null
        }
      >
        {items.length === 0 ? (
          <p>No due items right now.</p>
        ) : (
          items.map((item) => (
            <div key={item.id} className="crm-panel-card">
              <Stack gap={3}>
                <div className="notification-card__head">
                  <strong>{item.title}</strong>
                  {item.created_at ? (
                    <time className="notification-card__when" dateTime={item.created_at}>
                      {formatDateTime(item.created_at)}
                    </time>
                  ) : null}
                </div>
                <p className="crm-muted">{item.body}</p>
                <Cluster gap={2}>
                  {item.href ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setOpen(false);
                        navigate(item.href!);
                      }}
                    >
                      Open
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      void opsApi.dismissNotification(orgId, item.id).then(() => {
                        setItems((current) => current.filter((entry) => entry.id !== item.id));
                      });
                    }}
                  >
                    Dismiss
                  </Button>
                </Cluster>
              </Stack>
            </div>
          ))
        )}
      </Drawer>
    </>
  );
}
